import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Heart, Award } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1C5C62] to-[#14D1A2]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">About Healthwrx</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto">
            Wellbeing. Resilience. Xtra Life.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                At Healthwrx, we believe quality healthcare should be accessible to everyone. Our mission is to provide comprehensive, patient-centered medical services that prioritize your wellbeing and empower you to live a healthier, more resilient life.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We combine modern medical expertise with a compassionate approach, ensuring every patient receives personalized care in a comfortable, state-of-the-art environment.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Located in the heart of Guwahati, we serve our community with dedication, bringing together experienced doctors, advanced diagnostics, and convenient pharmacy services under one roof.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_care-staging/artifacts/v6xlqgyf_WhatsApp%20Image%202026-05-11%20at%2011.40.39.jpeg"
                alt="Modern clinic interior"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#14D1A2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-[#14D1A2]" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Compassion</h3>
              <p className="text-gray-600 leading-relaxed">
                We treat every patient with empathy, respect, and understanding, recognizing that healthcare is deeply personal.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#14D1A2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-[#14D1A2]" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Excellence</h3>
              <p className="text-gray-600 leading-relaxed">
                We maintain the highest standards in medical care, continuously updating our knowledge and technology.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#14D1A2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="w-8 h-8 text-[#14D1A2]" />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Integrity</h3>
              <p className="text-gray-600 leading-relaxed">
                We believe in transparent, honest communication and ethical practices in all aspects of healthcare delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-2xl order-2 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_care-staging/artifacts/fq2g3lnq_doctor-with-patient-s-x-ray-film.jpg.jpeg"
                alt="Medical professional with X-ray"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Modern Facilities</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Our clinic is designed with your comfort and care in mind. We've created a welcoming environment that combines medical excellence with patient comfort.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-[#14D1A2]/20 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-[#14D1A2]"></div>
                  </div>
                  <span className="text-gray-700">State-of-the-art diagnostic equipment</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-[#14D1A2]/20 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-[#14D1A2]"></div>
                  </div>
                  <span className="text-gray-700">Comfortable consultation rooms</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-[#14D1A2]/20 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-[#14D1A2]"></div>
                  </div>
                  <span className="text-gray-700">Well-stocked in-house pharmacy</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-[#14D1A2]/20 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-[#14D1A2]"></div>
                  </div>
                  <span className="text-gray-700">Spacious waiting areas</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-[#14D1A2]/20 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-[#14D1A2]"></div>
                  </div>
                  <span className="text-gray-700">Convenient parking facilities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1C5C62] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Experience Healthcare Done Right</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Visit us and discover the difference that compassionate, expert care can make
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-appointment">
              <Button size="lg" className="bg-[#14D1A2] hover:bg-[#10B88A] text-white text-lg px-8">
                Book Appointment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
